import { useEffect, useRef, useState } from 'react';
import Spinner from 'react-spinkit';
import { getCurrentYearTeachersData, getTotalRow } from '../reportUtilities';
import SortableTable from '../Tables/SortableTable';
import { cloneDeep } from 'lodash';
import styles from './TableStyles.module.scss';
import { ReportStudent, ReportingMixpanelEvent, TeacherClassroomMaps } from '@peekapak/types/ReportDataExchangeTypes';
import { getLegacyStudentLogins, getNewStudentLogins } from '../reportUtilities';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import TeacherClassroomModal from './TeacherClassroomModal';

interface OverallUsageProps {
  errorMessage: string;
  journalsMap: Record<string, unknown>;
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  userList: UserProfileType[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  gameProgressMap?: Record<
    string,
    Array<{
      completionPercent: number;
      displayName: string;
      id: string;
      lastDateOpened: number;
      readingLevel: number;
      timeSpent: number;
      type: string;
    }>
  >;
  assignmentsMap?: Record<string, Array<{ status: string }>>;
  startDate: Date | null;
  endDate: Date | null;
}

const StudentsSummary = ({
  teacherStudentMapReady,
  teacherStudentMap,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  userList,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  journalsMap,
  journalsMapReady,
  journalsMapLoadProgress,
  errorMessage,
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
  gameProgressMap,
  assignmentsMap,
  startDate,
  endDate,
}: OverallUsageProps) => {
  const [myState, setMyState] = useState({
    dataMap: {},
    dataMapReady: false,
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    teacherName: '',
    teacherEmail: '',
  });

  // const prevMixpanelEventsReady = useRef(
  //   legacyPeekavilleEventsReady &&
  //     legacyPlatformEventsReady &&
  //     newMixpanelEventsReady
  // );

  // useEffect(() => {
  //   if (
  //     prevMixpanelEventsReady.current &&
  //     (!legacyPeekavilleEventsReady ||
  //       !legacyPlatformEventsReady ||
  //       !newMixpanelEventsReady)
  //   ) {
  //     setMyState((prevState) => ({
  //       ...prevState,
  //       dataMap: {},
  //       dataMapReady: false,
  //     }));
  //   }
  //   prevMixpanelEventsReady.current =
  //     legacyPeekavilleEventsReady &&
  //     legacyPlatformEventsReady &&
  //     newMixpanelEventsReady;
  // }, [
  //   legacyPeekavilleEventsReady,
  //   legacyPlatformEventsReady,
  //   newMixpanelEventsReady,
  // ]);
  useEffect(() => {
    setMyState((prevState) => ({
      ...prevState,
      dataMap: {},
      dataMapReady: false,
    }));
  }, [legacyPlatformEvents, legacyPeekavilleEvents, newMixpanelEvents, journalsMap]);

  useEffect(() => {
    if (
      journalsMapReady &&
      legacyPeekavilleEventsReady &&
      teacherStudentMapReady &&
      teacherClassroomMapsReady &&
      legacyPlatformEventsReady &&
      newMixpanelEventsReady &&
      !myState.dataMapReady
    ) {
      buildData();
    }

    function buildData() {
      const newMap = {};
      const combinedEvents = [...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents];

      const allSeen = new Set<string>();
      Object.keys(teacherStudentMap).forEach((teacherKey) => {
        const newStudentsArray = teacherStudentMap[teacherKey]
          .filter((s) => {
            if (allSeen.has(s.userId)) return false;
            allSeen.add(s.userId);
            return true;
          })
          .map((s) => {
            const events = combinedEvents.filter((event) => (event.userId || event.distinct_id) === s.userId);

            return {
              ...s,
              events,
              journals: journalsMap[s.userId],
            };
          });

        newMap[teacherKey] = newStudentsArray;
      });

      setMyState((prevState) => ({
        ...prevState,
        dataMap: newMap,
        dataMapReady: true,
      }));
    }
  }, [
    teacherClassroomMapsReady,
    legacyPeekavilleEventsReady,
    legacyPeekavilleEvents,
    teacherStudentMap,
    teacherStudentMapReady,
    journalsMap,
    journalsMapReady,
    myState.dataMapReady,
    legacyPlatformEventsReady,
    newMixpanelEventsReady,
    userList,
  ]);

  const message = getDataNotReadyMessage();
  if (message) {
    return message;
  }

  const staticMessage = getStaticMessage();
  if (staticMessage) {
    return staticMessage;
  }

  const rosteredTeachers = getCurrentYearTeachersData(
    userList,
    teacherClassroomMaps,
    legacyPlatformEvents,
    newMixpanelEvents,
  );

  const sortedList = cloneDeep(rosteredTeachers).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  const combinedEvents = [...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents];

  const rosteredStudentIds = new Set(
    Object.values(teacherStudentMap)
      .flat()
      .map((s) => s.userId),
  );

  const allStudentIds = [
    ...new Set(
      combinedEvents
        .filter((e) => e.type === 'Student')
        .map((e) => e.userId || e.distinct_id)
        .filter(Boolean),
    ),
  ];
  const otherStudentIds = allStudentIds.filter((id) => !rosteredStudentIds.has(id));

  const otherEvents = combinedEvents.filter(
    (e) => (e.userId || e.distinct_id) && otherStudentIds.includes(e.userId || e.distinct_id),
  );

  const tableData = sortedList.map((e) => {
    return {
      teacher_name: `${e.firstName || ''} ${e.lastName || ''}`,
      number_of_students: getNumberOfRosteredStudents(e.email),
      student_logins: getNumberOfStudentLogins(e.email),
      feelings_checkins: getNumberOfJournals(e.email),
      email: e.email,
      userId: e.userId,
    };
  });

  if (otherStudentIds.length > 0) {
    const otherJournals = otherStudentIds.reduce((acc, id) => acc + ((journalsMap[id] as any[])?.length || 0), 0);
    tableData.push({
      teacher_name: 'Other (Unassociated)',
      number_of_students: otherStudentIds.length,
      student_logins: getLegacyStudentLogins(otherEvents) + getNewStudentLogins(otherEvents),
      feelings_checkins: otherJournals,
      email: null,
      userId: null,
    });
  }

  const handleTeacherClick = (data: { teacher_name?: string; email?: string }) => {
    if (!data.email) return;
    setModalState({
      isOpen: true,
      teacherName: data.teacher_name || '',
      teacherEmail: data.email || '',
    });
  };

  const columns = [
    {
      label: "Teacher's Name",
      accessor: 'teacher_name',
      sortable: false,
      clickable: (data: any) => data.email != null,
      onClick: handleTeacherClick,
      renderTooltip: (data: { teacher_name?: string }) =>
        data.teacher_name === 'Other (Unassociated)'
          ? 'Any data that cannot be associated with a user. This is mainly due to the original accounts and/or classrooms having been deleted'
          : `View student breakdown by classroom for ${data.teacher_name || ''}`,
    },
    {
      label: 'Number of Students',
      accessor: 'number_of_students',
      sortable: true,
      tooltip: 'Number of students currently rostered in a classroom associated with this teacher.',
    },
    {
      label: 'Student Logins',
      accessor: 'student_logins',
      sortable: true,
      tooltip: 'Logins by students currently rostered in a classroom associated with this teacher.',
    },
    {
      label: 'Feelings Check-Ins',
      accessor: 'feelings_checkins',
      sortable: true,
      tooltip:
        'Check-ins in the myPeekaville game or on the student platform by students currently rostered in a classroom associated with this teacher.',
    },
  ];

  const totalRow = getTotalRow(tableData);

  const getTeacherData = () => {
    const teacher = teacherClassroomMaps.maps.find((t) => t.email === modalState.teacherEmail);
    return teacher || null;
  };

  // console.debug(
  //   `%cStudent Analytics active students`,
  //   'background: blue; color: yellow',
  //   totalActiveStudents
  // );

  const teacherData = getTeacherData();

  return (
    <div>
      <SortableTable data={tableData} columns={columns} totals={totalRow} />
      {teacherData && (
        <TeacherClassroomModal
          isOpen={modalState.isOpen}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
          teacherName={modalState.teacherName}
          teacherEmail={modalState.teacherEmail}
          classrooms={teacherData.classrooms}
          allEvents={[...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents]}
          journalsMap={journalsMap}
          gameProgressMap={gameProgressMap}
          assignmentsMap={assignmentsMap}
        />
      )}
    </div>
  );

  function getStaticMessage() {
    if (userList.length === 0) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>There are no users in this school</div>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {`We're sorry, there's a problem retrieving data. Please contact
            Peekapak support and provide them with this error message:`}
            {errorMessage}
          </div>
        </div>
      );
    }
  }

  function getDataNotReadyMessage() {
    let message = null;

    if (!teacherStudentMapReady) {
      message = 'Matching students to teachers...';
    } else if (!legacyPeekavilleEventsReady) {
      message = 'Loading game events data...';
    } else if (!journalsMapReady) {
      message = `Loading student check-in data ${journalsMapLoadProgress}...`;
    } else if (!myState.dataMapReady) {
      message = 'Calculating stats...';
    }

    if (message) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {message}
            <Spinner name='line-scale' />
            This may take up to 10 minutes or more
          </div>
        </div>
      );
    }

    return null;
  }

  function getNumberOfJournals(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      const numberOfJournalEntries = studentArray.map((s) => {
        if (s.journals) {
          return s.journals.reduce((acc, j) => acc + 1, 0);
        }

        return 0;
      });

      return numberOfJournalEntries.reduce((acc, e) => acc + e, 0);
    }
    return 0;
  }

  function getNumberOfStudentLogins(email: string) {
    const studentArray = myState.dataMap[email];
    const eventsArray = studentArray.reduce((acc, s) => [...acc, ...s.events], []);

    return getLegacyStudentLogins(eventsArray) + getNewStudentLogins(eventsArray);
  }

  function getNumberOfRosteredStudents(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      return studentArray.length;
    }
    return 0;
  }
};

export default StudentsSummary;
